import React, { useState } from 'react';
import { flagshipProject } from '../data/projects';
import { PipelineStage } from '../types';

interface NodePosition {
  x: number;
  y: number;
  w: number;
  h: number;
  step: string;
  title: string;
  sub: string;
}

const NODE_POSITIONS: readonly NodePosition[] = [
  { x: 30, y: 106, w: 156, h: 56, step: '01', title: 'ROUTING', sub: 'FastAPI / OpenAI' },
  { x: 236, y: 38, w: 156, h: 56, step: '02', title: 'SCHEMA RAG', sub: 'pgvector (400+ tbls)' },
  { x: 442, y: 106, w: 156, h: 56, step: '03', title: 'LANGGRAPH', sub: 'AST SELECT Guard' },
  { x: 648, y: 38, w: 156, h: 56, step: '04', title: 'GOVERNANCE', sub: 'Keycloak & HITL' },
  { x: 854, y: 106, w: 156, h: 56, step: '05', title: 'DELIVERY', sub: 'Async Chat & Drive' }
];

export default function Pipeline(): React.JSX.Element {
  const [activeStageIndex, setActiveStageIndex] = useState<number>(0);
  const stages: readonly PipelineStage[] = flagshipProject.pipelineStages;
  const currentStage: PipelineStage = stages[activeStageIndex];

  return (
    <div className="pipeline-container">
      <div className="pipeline-header">
        <div className="pipeline-title-group">
          <span className="pipeline-badge">Interactive Architecture Flow</span>
          <h3 className="pipeline-heading">Production AI Natural Language-to-SQL Agent Pipeline</h3>
        </div>
      </div>

      <div className="pipeline-nav-buttons" role="tablist" aria-label="Pipeline Stages">
        {stages.map((stg, idx) => (
          <button
            key={stg.id}
            role="tab"
            aria-selected={activeStageIndex === idx}
            aria-controls={`stage-panel-${stg.id}`}
            id={`stage-tab-${stg.id}`}
            className={`stage-pill-btn ${activeStageIndex === idx ? 'active' : ''}`}
            onClick={() => setActiveStageIndex(idx)}
          >
            <span className="pill-num">{stg.step}</span>
            <span className="pill-name">{stg.title.split(' ')[0]}</span>
          </button>
        ))}
      </div>

      {/* Desktop & Tablet Horizontal SVG Flow (>= 768px) */}
      <div className="pipeline-desktop-flow">
        <div className="pipeline-svg-box">
          <svg
            className="pipeline-svg"
            viewBox="0 0 1040 200"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="5-stage architecture pipeline diagram of the NL-to-SQL AI agent"
          >
            <defs>
              <linearGradient id="pipelineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="var(--teal)" stopOpacity="0.8" />
                <stop offset="50%" stopColor="var(--amber)" stopOpacity="0.9" />
                <stop offset="100%" stopColor="var(--teal)" stopOpacity="0.8" />
              </linearGradient>
              <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>

            {/* Background tracks */}
            <path className="flow-line-bg" d="M 108,134 C 188,134 234,66 314,66 C 394,66 440,134 520,134 C 600,134 646,66 726,66 C 806,66 852,134 932,134" />
            <path className="flow-line-active" d="M 108,134 C 188,134 234,66 314,66 C 394,66 440,134 520,134 C 600,134 646,66 726,66 C 806,66 852,134 932,134" />

            {/* Animated signal pulse along track */}
            <circle r="5" fill="var(--amber-bright)" filter="url(#glow)">
              <animateMotion
                dur="6.5s"
                repeatCount="indefinite"
                path="M 108,134 C 188,134 234,66 314,66 C 394,66 440,134 520,134 C 600,134 646,66 726,66 C 806,66 852,134 932,134"
              />
            </circle>

            {/* Interactive Nodes */}
            {NODE_POSITIONS.map((node, i) => {
              const isSelected = activeStageIndex === i;
              return (
                <g
                  key={node.step}
                  className={`pipeline-node-g ${isSelected ? 'selected' : ''}`}
                  onClick={() => setActiveStageIndex(i)}
                  tabIndex={0}
                  role="button"
                  aria-label={`Select stage ${node.step}: ${node.title}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setActiveStageIndex(i);
                    }
                  }}
                >
                  <rect
                    className={`node-rect ${isSelected ? 'active-rect' : ''}`}
                    x={node.x}
                    y={node.y}
                    width={node.w}
                    height={node.h}
                    rx="6"
                  />
                  <circle
                    className={`node-status-dot ${isSelected ? 'active-dot' : ''}`}
                    cx={node.x + 14}
                    cy={node.y + 18}
                    r="3.5"
                  />
                  <text className="node-step-txt" x={node.x + 25} y={node.y + 22}>
                    {node.step}
                  </text>
                  <text className="node-title-txt" x={node.x + 14} y={node.y + 36}>
                    {node.title}
                  </text>
                  <text className="node-sub-txt" x={node.x + 14} y={node.y + 48}>
                    {node.sub}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* Mobile Vertical Stepped Timeline (< 768px) */}
      <div className="pipeline-mobile-flow" aria-label="Mobile Pipeline Stages">
        <div className="mobile-flow-timeline">
          {stages.map((stg, idx) => {
            const isSelected = activeStageIndex === idx;
            return (
              <div
                key={`mobile-stage-${stg.id}`}
                className={`mobile-flow-step ${isSelected ? 'active' : ''}`}
                role="button"
                aria-pressed={isSelected}
                aria-label={`Select mobile stage ${stg.step}: ${stg.title}`}
                id={`stage-mobile-${stg.id}`}
                tabIndex={0}
                onClick={() => setActiveStageIndex(idx)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setActiveStageIndex(idx);
                  }
                }}
              >
                <div className="mobile-step-indicator">
                  <div className="mobile-step-badge">{stg.step}</div>
                  {idx < stages.length - 1 && <div className="mobile-step-connector" aria-hidden="true" />}
                </div>

                <div className="mobile-step-card">
                  <div className="mobile-step-card-header">
                    <span className="mobile-step-title">{stg.title}</span>
                    <span className="mobile-step-tech">{NODE_POSITIONS[idx]?.sub || stg.tech.split('·')[0].trim()}</span>
                  </div>
                  <p className="mobile-step-desc">{stg.shortDesc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Stage Live Inspector Panel */}
      <div
        className="stage-inspector"
        id={`stage-panel-${currentStage.id}`}
        role="tabpanel"
        aria-labelledby={`stage-tab-${currentStage.id}`}
      >
        <div className="inspector-top">
          <div className="inspector-step-tag">
            <span className="step-tag-num">STAGE {currentStage.step} OF 05</span>
            <span className="step-tag-title">{currentStage.title}</span>
          </div>
          <div className="inspector-tech-badge">
            <code>{currentStage.tech}</code>
          </div>
        </div>

        <p className="inspector-desc">{currentStage.fullDesc}</p>

        <div className="inspector-highlights">
          <div className="highlights-label">Architecture Invariants &amp; Guardrails:</div>
          <ul className="highlights-list">
            {currentStage.highlights.map((hl) => (
              <li key={`${currentStage.id}-hl-${hl.slice(0, 24)}`}>
                <svg className="check-icon" width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M16.6666 5L7.49992 14.1667L3.33325 10" stroke="var(--teal-bright)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{hl}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
